import React from 'react';
import { CheckCircle } from 'lucide-react';
import {
  type Plan,
  getStripeFeatureStringsForTier,
  resolveSubscriptionTierForDisplay,
} from '@court-sync/shared';

function getFullFeaturesForPlan(plan: Plan): string[] {
  const tier = resolveSubscriptionTierForDisplay(plan.name, plan.internalId ?? null);
  return getStripeFeatureStringsForTier(tier);
}

export interface FinishSignupFeatureComparisonProps {
  plans: Plan[];
  className?: string;
}

/** Full marketing feature lists for finish-signup plan comparison. */
export function FinishSignupFeatureComparison({ plans, className }: FinishSignupFeatureComparisonProps) {
  if (plans.length === 0) return null;

  const gridCols =
    plans.length === 1
      ? 'grid-cols-1'
      : plans.length === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1 md:grid-cols-3';

  return (
    <div className={className}>
      <div className="overflow-x-auto pb-1">
        <div className={`grid min-w-0 gap-3 ${gridCols}`}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className="rounded-lg border border-slate-200 bg-slate-50/60 dark:border-slate-700 dark:bg-slate-900/40"
            >
              <div className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">
                <h3 className="text-xs font-semibold text-slate-900 dark:text-white">{plan.name}</h3>
              </div>
              <ul
                className="space-y-1 px-3 py-2.5 text-[11px] leading-snug text-slate-600 dark:text-slate-400"
                aria-label={`${plan.name} full features`}
              >
                {getFullFeaturesForPlan(plan).map((line, i) => (
                  <li key={i} className="flex gap-1.5">
                    <CheckCircle
                      className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-500"
                      aria-hidden
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
